import type { InvoiceRetention, InvoiceSiteLevy } from '../../../types';
import { roundMoney } from './finalInvoiceTotals';

export type { InvoiceRetention, InvoiceSiteLevy };

export const DEFAULT_SITE_LEVY_LABEL = 'Umlage Baunebenkosten';

export function normalizeSiteLevy(raw: unknown): InvoiceSiteLevy | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const label =
    typeof o.label === 'string' && o.label.trim() ? o.label.trim().slice(0, 120) : DEFAULT_SITE_LEVY_LABEL;
  let percent: number | null = null;
  if (o.percent != null && o.percent !== '') {
    const n = typeof o.percent === 'number' ? o.percent : parseFloat(String(o.percent).replace(',', '.'));
    if (Number.isFinite(n) && n > 0) percent = Math.min(100, Math.round(n * 100) / 100);
  }
  let amountNet: number | null = null;
  if (o.amountNet != null && o.amountNet !== '') {
    const n = typeof o.amountNet === 'number' ? o.amountNet : parseFloat(String(o.amountNet).replace(',', '.'));
    if (Number.isFinite(n) && n > 0) amountNet = roundMoney(n);
  }
  if (percent == null && amountNet == null) return null;
  return {
    kind: 'site_levy',
    label,
    percent,
    amountNet: percent != null ? null : amountNet,
  };
}

export function normalizeRetention(raw: unknown): InvoiceRetention | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  let percent: number | null = null;
  if (o.percent != null && o.percent !== '') {
    const n = typeof o.percent === 'number' ? o.percent : parseFloat(String(o.percent).replace(',', '.'));
    if (Number.isFinite(n) && n > 0) percent = Math.min(100, Math.round(n * 100) / 100);
  }
  let amountGross: number | null = null;
  if (o.amountGross != null && o.amountGross !== '') {
    const n =
      typeof o.amountGross === 'number' ? o.amountGross : parseFloat(String(o.amountGross).replace(',', '.'));
    if (Number.isFinite(n) && n > 0) amountGross = roundMoney(n);
  }
  if (percent == null && amountGross == null) return null;
  return {
    kind: 'performance',
    percent,
    amountGross: percent != null ? null : amountGross,
  };
}

export function computeSiteLevyNet(netBeforeLevy: number, levy: InvoiceSiteLevy | null | undefined): number {
  const base = Math.max(0, Number(netBeforeLevy) || 0);
  if (!levy) return 0;
  if (levy.percent != null && levy.percent > 0) {
    return roundMoney(Math.min(base, (base * levy.percent) / 100));
  }
  if (levy.amountNet != null && levy.amountNet > 0) {
    return roundMoney(Math.min(base, levy.amountNet));
  }
  return 0;
}

const RETROACTIVE_RETENTION_DOCUMENT_TYPES = new Set(['invoice', 'partial_invoice', 'final_invoice']);

export type RetroactiveRetentionPatch = {
  retention_gross: number | null;
  retention_status: 'none' | 'held';
  payable_amount: number;
  skonto_amount?: number | null;
  paid_amount?: number | null;
  retention_held?: boolean;
};

export type RetroactiveRetentionResult =
  | { ok: true; patch: RetroactiveRetentionPatch }
  | { ok: false; error: string };

/**
 * Nachträglicher Sicherheitseinbehalt auf einer bereits erfassten Ausgangsrechnung.
 * Nennbeträge bleiben unverändert; nur Zahlbetrag und Einbehalt werden angepasst.
 * Schlussrechnungen behalten bereits addierte Vor-Einbehalte über das Delta.
 */
export function applyRetroactiveRetention(input: {
  enabled: boolean;
  documentType: string;
  status: string;
  retentionStatus?: string | null;
  grossAmount: number;
  payableAmount?: number | null;
  retentionGross?: number | null;
  skontoAmount?: number | null;
  paidAt?: string | null;
  paidAmount?: number | null;
  retentionPercent: number;
}): RetroactiveRetentionResult {
  if (!input.enabled) {
    return { ok: false, error: 'Umlage und Sicherheitseinbehalt sind nicht aktiviert.' };
  }
  if (!RETROACTIVE_RETENTION_DOCUMENT_TYPES.has(input.documentType)) {
    return { ok: false, error: 'Einbehalt kann nur auf Ausgangsrechnungen eingetragen werden.' };
  }
  if (input.status === 'cancelled') {
    return { ok: false, error: 'Auf eine stornierte Rechnung kann kein Einbehalt eingetragen werden.' };
  }
  if (input.retentionStatus === 'released_on_final') {
    return { ok: false, error: 'Der Einbehalt wurde bereits auf der Schlussrechnung aufgelöst.' };
  }
  const percent = Number(input.retentionPercent);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
    return { ok: false, error: 'Einbehalt muss zwischen 0 und 100 Prozent liegen.' };
  }

  const gross = roundMoney(Math.max(0, Number(input.grossAmount) || 0));
  const oldRetention = roundMoney(Math.max(0, Number(input.retentionGross) || 0));
  const payableRaw = input.payableAmount;
  const oldPayable = payableRaw == null || !Number.isFinite(Number(payableRaw))
    ? gross
    : roundMoney(Math.max(0, Number(payableRaw)));
  const newRetention = percent > 0 ? roundMoney(Math.min(gross, (gross * percent) / 100)) : 0;
  const payableAmount = roundMoney(Math.max(0, oldPayable - (newRetention - oldRetention)));
  const patch: RetroactiveRetentionPatch = {
    retention_gross: newRetention > 0 ? newRetention : null,
    retention_status: newRetention > 0 ? 'held' : 'none',
    payable_amount: payableAmount,
  };

  const skontoRaw = input.skontoAmount;
  if (skontoRaw != null && Number.isFinite(Number(skontoRaw)) && Number(skontoRaw) > payableAmount) {
    patch.skonto_amount = payableAmount > 0 ? payableAmount : null;
  }

  const paidRaw = input.paidAmount;
  const paid = paidRaw == null || !Number.isFinite(Number(paidRaw)) ? null : roundMoney(Number(paidRaw));
  if (input.paidAt && paid != null && Math.abs(paid - oldPayable) < 0.001) {
    patch.paid_amount = payableAmount;
    patch.retention_held = newRetention > 0;
  }

  return { ok: true, patch };
}

export function computeRetentionGross(
  faceGross: number,
  retention: InvoiceRetention | null | undefined,
): number {
  const base = Math.max(0, Number(faceGross) || 0);
  if (!retention) return 0;
  if (retention.percent != null && retention.percent > 0) {
    return roundMoney(Math.min(base, (base * retention.percent) / 100));
  }
  if (retention.amountGross != null && retention.amountGross > 0) {
    return roundMoney(Math.min(base, retention.amountGross));
  }
  return 0;
}

export function computeSkontoAmount(payableBeforeSkonto: number, skontoPercent: number | null | undefined): number {
  const base = Math.max(0, Number(payableBeforeSkonto) || 0);
  const pct = Number(skontoPercent);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return roundMoney((base * Math.min(100, pct)) / 100);
}

export type InvoicePayableTotalsInput = {
  /** Netto-Nennwert dieser Rechnung (nach Abschlag-Kette, vor Umlage). */
  faceNetBeforeLevy: number;
  /**
   * USt-Nennwert vor Umlage (Abschlagskette). Ohne Umlage unverändert übernehmen,
   * damit Cent-Differenzen aus Prior-Abzügen erhalten bleiben.
   */
  faceVatBeforeLevy?: number;
  vatRatePercent: number;
  siteLevy?: InvoiceSiteLevy | null;
  retention?: InvoiceRetention | null;
  /** Summe Einbehalte aus abgezogenen Abschlägen, die auf der Schlussrechnung fällig werden. */
  priorRetentionsDueGross?: number;
  skontoPercent?: number | null;
};

export type InvoicePayableTotals = {
  siteLevyNet: number;
  /** Nennwert Netto nach Umlage (wird in invoice_documents.net_amount gespeichert). */
  faceNet: number;
  faceVat: number;
  faceGross: number;
  retentionGross: number;
  priorRetentionsDueGross: number;
  /** Sofort fälliger Zahlbetrag (ohne Skontoziehung). */
  payableAmount: number;
  skontoAmount: number;
  /** Zahlbetrag bei Skontoziehung. */
  payableWithSkonto: number;
};

/**
 * Face amounts (net/vat/gross) include site levy but never retention or Skonto.
 * Payable = faceGross − retention + prior retentions due on final.
 */
export function computeInvoicePayableTotals(input: InvoicePayableTotalsInput): InvoicePayableTotals {
  const beforeLevy = roundMoney(Math.max(0, Number(input.faceNetBeforeLevy) || 0));
  const siteLevyNet = computeSiteLevyNet(beforeLevy, input.siteLevy);
  const vatRate = Math.max(0, Number(input.vatRatePercent) || 0) / 100;
  let faceNet: number;
  let faceVat: number;
  if (siteLevyNet > 0) {
    faceNet = roundMoney(Math.max(0, beforeLevy - siteLevyNet));
    faceVat = roundMoney(faceNet * vatRate);
  } else {
    faceNet = beforeLevy;
    const providedVat = Number(input.faceVatBeforeLevy);
    faceVat = Number.isFinite(providedVat)
      ? roundMoney(Math.max(0, providedVat))
      : roundMoney(faceNet * vatRate);
  }
  const faceGross = roundMoney(faceNet + faceVat);
  const retentionGross = computeRetentionGross(faceGross, input.retention);
  const priorRetentionsDueGross = roundMoney(Math.max(0, Number(input.priorRetentionsDueGross) || 0));
  const payableAmount = roundMoney(Math.max(0, faceGross - retentionGross + priorRetentionsDueGross));
  const skontoAmount = computeSkontoAmount(payableAmount, input.skontoPercent);
  const payableWithSkonto = roundMoney(Math.max(0, payableAmount - skontoAmount));
  return {
    siteLevyNet,
    faceNet,
    faceVat,
    faceGross,
    retentionGross,
    priorRetentionsDueGross,
    payableAmount,
    skontoAmount,
    payableWithSkonto,
  };
}
